import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption, MatSelect } from "@angular/material/select";
import {
  DEVICE_ATTRIBUTE_COLUMN_PREFIX,
  ProductAttributeScope,
  ProductCategory,
  ProductCategoryAttribute,
} from "../../../master-data/master-data.model";

export interface DeviceAttributeFieldDialogData {
  categories: ProductCategory[];
  /** Render tags of the template's fields: their attributes are already added. */
  existingRenderTags: string[];
  /** Section names, by section index. */
  sections: string[];
}

export interface DeviceAttributeFieldDialogResult {
  attributes: ProductCategoryAttribute[];
  /** Section index, or null for the top-level fields. */
  section: number | null;
}

/** Picks the editable (DEVICE-scope) attributes of a category to add as "devAttr_<key>" fields. */
@Component({
  selector: "app-device-attribute-field-dialog",
  imports: [
    FormsModule,
    MatButton,
    MatCheckbox,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: "./device-attribute-field-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DeviceAttributeFieldDialogComponent {
  private dialogRef =
    inject<MatDialogRef<DeviceAttributeFieldDialogComponent, DeviceAttributeFieldDialogResult>>(
      MatDialogRef,
    );
  data = inject<DeviceAttributeFieldDialogData>(MAT_DIALOG_DATA);

  categoryUuid: string | null = null;
  section: number | null = null;
  selected = new Set<string>();

  /** Categories with editable attributes. */
  categories(): ProductCategory[] {
    return this.data.categories.filter((category) => this.attributesOf(category).length > 0);
  }

  attributes(): ProductCategoryAttribute[] {
    const category = this.data.categories.find((c) => c.uuid === this.categoryUuid);
    return category ? this.attributesOf(category) : [];
  }

  private attributesOf(category: ProductCategory): ProductCategoryAttribute[] {
    return [...(category.productCategoryAttributes ?? [])]
      .filter((attribute) => attribute.scope === ProductAttributeScope.DEVICE && !!attribute.key)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  }

  isAdded(attribute: ProductCategoryAttribute): boolean {
    return this.data.existingRenderTags.includes(DEVICE_ATTRIBUTE_COLUMN_PREFIX + attribute.key);
  }

  categoryChanged() {
    this.selected.clear();
  }

  toggle(attribute: ProductCategoryAttribute, checked: boolean) {
    if (checked) {
      this.selected.add(attribute.key);
    } else {
      this.selected.delete(attribute.key);
    }
  }

  add() {
    this.dialogRef.close({
      attributes: this.attributes().filter((attribute) => this.selected.has(attribute.key)),
      section: this.section,
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
